import { useState, useMemo } from "react";
import { Country, City } from "country-state-city";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface LocationPickerProps {
  defaultCountry?: string | null;
  defaultCity?: string | null;
  disabled?: boolean;
  countryLabel?: string;
  cityLabel?: string;
}

const ALL_COUNTRIES = Country.getAllCountries().sort((a, b) => a.name.localeCompare(b.name));

export function LocationPicker({
  defaultCountry,
  defaultCity,
  disabled = false,
  countryLabel = "Country",
  cityLabel = "City",
}: LocationPickerProps) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry ?? "");
  const [selectedCity, setSelectedCity] = useState<string>(defaultCity ?? "");

  const cities = useMemo(() => {
    if (!selectedCountry) return [];
    const raw = City.getCitiesOfCountry(selectedCountry) ?? [];
    const unique = Array.from(new Map(raw.map(c => [c.name, c])).values());
    return unique.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCountry]);

  const countryName = selectedCountry
    ? (Country.getCountryByCode(selectedCountry)?.name ?? selectedCountry)
    : "";

  const handleCountrySelect = (isoCode: string) => {
    const code = isoCode === "__none__" ? "" : isoCode;
    setSelectedCountry(code);
    setSelectedCity("");
    setCountryOpen(false);
  };

  return (
    <>
      {/* Hidden inputs for form submission */}
      <input type="hidden" name="locationCountry" value={selectedCountry} />
      <input type="hidden" name="locationCity" value={selectedCity} />

      {/* Country */}
      <div className="space-y-2">
        <Label>{countryLabel}</Label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className={countryName ? "text-foreground" : "text-muted-foreground"}>
                {countryName || "Select country…"}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country…" />
              <CommandList className="max-h-64">
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="__none__" onSelect={() => handleCountrySelect("__none__")}>
                    <Check className={`mr-2 h-4 w-4 ${!selectedCountry ? "opacity-100" : "opacity-0"}`} />
                    — None —
                  </CommandItem>
                  {ALL_COUNTRIES.map(c => (
                    <CommandItem
                      key={c.isoCode}
                      value={c.name}
                      onSelect={() => handleCountrySelect(c.isoCode)}
                    >
                      <Check className={`mr-2 h-4 w-4 ${selectedCountry === c.isoCode ? "opacity-100" : "opacity-0"}`} />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* City */}
      <div className="space-y-2">
        <Label>{cityLabel}</Label>
        {cities.length > 0 ? (
          <Popover open={cityOpen} onOpenChange={setCityOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className="flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className={selectedCity ? "text-foreground" : "text-muted-foreground"}>
                  {selectedCity || "Select city…"}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search city…" />
                <CommandList className="max-h-64">
                  <CommandEmpty>No city found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="__none__" onSelect={() => { setSelectedCity(""); setCityOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${!selectedCity ? "opacity-100" : "opacity-0"}`} />
                      — None —
                    </CommandItem>
                    {cities.map(c => (
                      <CommandItem
                        key={c.name}
                        value={c.name}
                        onSelect={() => { setSelectedCity(c.name); setCityOpen(false); }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${selectedCity === c.name ? "opacity-100" : "opacity-0"}`} />
                        {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Input
            disabled={disabled}
            value={selectedCity}
            onChange={e => setSelectedCity(e.target.value)}
            placeholder={selectedCountry ? "Enter city name…" : "Select a country first"}
            className="rounded-xl"
          />
        )}
      </div>
    </>
  );
}
